import type { CmmdRuntimeDiagnosticsSnapshot } from "@cmmd-forge/contracts";

import { createCmmdApiClient } from "../../environments/cmmdApi";
import {
  buildPrimaryFetchInit,
  resolvePrimaryEnvironmentHttpUrl,
} from "../../environments/primary";
import { ensureSeatEnvironment } from "../../lib/firstRunEnvironment";
import { refreshCmmdForgeRuntimeList } from "../../cmmd/cmmdForgeRuntimeListResource";
import {
  resolveCurrentForgeWorkspace,
  type ForgeWorkspaceMembership,
} from "../../workspace/resolveCurrentForgeWorkspace";
import {
  actualsFromDiagnostics,
  diffPreparedContract,
  expectedFromSnapshot,
  firstRunSucceeds,
} from "../settings/preparedContractDiff";

/**
 * `confirm` is the gate: CMMD resolved an organization for this Seat but has
 * not been asked to create anything in it yet. Only `ensure()` — the explicit
 * click this hook exposes — moves the phase to `ensuring` and sends the POST.
 * See CMMD-Center/forge#3573: before this phase existed, the mount effect
 * posted ensure on its own and created a cloud runtime in whichever
 * organization it guessed, with no click.
 */
export type FirstRunEnvironmentPhase =
  | "idle"
  | "confirm"
  | "ensuring"
  | "ready"
  | "dead-end"
  | "failed";

export interface FirstRunEnvironmentState {
  readonly catalogEnvironmentId: string | null;
  readonly environmentError: string | null;
  readonly environmentId: string | null;
  readonly environmentPhase: FirstRunEnvironmentPhase;
  /** The CMMD organization `ensure()` will post to. Set once phase is `confirm`. */
  readonly pendingOrganizationId: number | null;
  /**
   * The name to show for that organization. Falls back to `organization
   * <id>` when CMMD's bootstrap did not return a name for it, so the control
   * never reads "Create cloud runtime in null".
   */
  readonly pendingWorkspaceLabel: string | null;
  /**
   * Populated only in `confirm` phase, only when the account belongs to more
   * than one CMMD workspace and has not picked one yet (CMMD-Center/forge#3572):
   * `pendingOrganizationId` stays `null` and the control must render this list
   * instead of a single "Create cloud runtime in …" button, so nothing is
   * created until `selectWorkspace` names one.
   */
  readonly workspaceOptions: ReadonlyArray<ForgeWorkspaceMembership>;
  /** Picks one of `workspaceOptions`. No-op once `pendingOrganizationId` is already set. */
  readonly selectWorkspace: (organizationId: number) => void;
  /** Posts the ensure for `pendingOrganizationId`. No-op before it resolves or after the first call. */
  readonly ensure: () => void;
}

const CONTRACT_MISS_COPY =
  "This Environment is not prepared. Open Diagnostics and Recheck to see the contract misses.";

async function snapshotForRuntime(
  runtimeId: string,
): Promise<CmmdRuntimeDiagnosticsSnapshot | undefined> {
  try {
    return await createCmmdApiClient().getRuntimeDiagnostics(runtimeId);
  } catch {
    return undefined;
  }
}

/**
 * What the resolve effect should do once CMMD's bootstrap answers, pulled out
 * as a pure function so the effect body stays a straight dispatch instead of
 * nested branching (CMMD-Center/forge#3572).
 */
type ResolveOutcome =
  | { readonly kind: "confirm"; readonly organizationId: number; readonly workspaceName: string }
  | { readonly kind: "ask"; readonly options: ReadonlyArray<ForgeWorkspaceMembership> }
  | { readonly kind: "failed"; readonly message: string };

function resolveFirstRunOutcome(input: {
  /** A project already registered in a known organization: a fact, not a guess. */
  readonly catalogOrganizationId: number | null;
  readonly currentWorkspaceId: number | null;
  readonly workspaces: ReadonlyArray<ForgeWorkspaceMembership>;
}): ResolveOutcome {
  if (input.catalogOrganizationId !== null) {
    const known = input.workspaces.find(
      (workspace) => workspace.id === input.catalogOrganizationId,
    );
    return {
      kind: "confirm",
      organizationId: input.catalogOrganizationId,
      workspaceName: known?.name ?? `organization ${input.catalogOrganizationId}`,
    };
  }
  const resolution = resolveCurrentForgeWorkspace({
    storedWorkspaceId: input.currentWorkspaceId,
    workspaces: input.workspaces,
  });
  if (resolution.kind === "none") {
    return { kind: "failed", message: "CMMD did not return an organization." };
  }
  if (resolution.kind === "ask") {
    return { kind: "ask", options: resolution.options };
  }
  return {
    kind: "confirm",
    organizationId: resolution.organizationId,
    workspaceName: resolution.workspaceName,
  };
}

function firstRunReadyFromSnapshot(input: {
  readonly environmentId: string;
  readonly snapshot: CmmdRuntimeDiagnosticsSnapshot | undefined;
}): boolean {
  const expected = expectedFromSnapshot(input.snapshot);
  const rows = diffPreparedContract({
    expected,
    actuals: actualsFromDiagnostics({
      guestForgeCommit: input.snapshot?.forge.installedCommit,
      supervisedServiceObserved: input.snapshot !== undefined,
      toolNames: [],
      replicationAvailable:
        input.snapshot === undefined ? null : input.snapshot.replication.state === "available",
    }),
  });
  return firstRunSucceeds({
    expected,
    environmentId: input.environmentId,
    rows,
  });
}

export interface FirstRunResolveSetters {
  readonly setEnvironmentPhase: (phase: FirstRunEnvironmentPhase) => void;
  readonly setPendingOrganizationId: (organizationId: number | null) => void;
  readonly setPendingWorkspaceLabel: (label: string | null) => void;
  readonly setWorkspaceOptions: (options: ReadonlyArray<ForgeWorkspaceMembership>) => void;
  readonly setEnvironmentError: (message: string | null) => void;
}

/** Runs the resolve effect's bootstrap fetch and applies whatever it decides. */
export async function runResolveAndApply(
  input: {
    readonly catalogOrganizationId: number | null;
    readonly currentWorkspaceId: number | null;
  },
  setters: FirstRunResolveSetters,
): Promise<void> {
  try {
    const bootstrap = await createCmmdApiClient().getBootstrap(
      input.currentWorkspaceId !== null ? { organizationId: input.currentWorkspaceId } : undefined,
    );
    const outcome = resolveFirstRunOutcome({
      catalogOrganizationId: input.catalogOrganizationId,
      currentWorkspaceId: input.currentWorkspaceId,
      workspaces: bootstrap.workspaces.map((workspace) => ({
        id: workspace.id,
        name: workspace.name,
      })),
    });
    applyResolveOutcome(outcome, setters);
  } catch (error) {
    setters.setEnvironmentPhase("failed");
    setters.setEnvironmentError(
      error instanceof Error ? error.message : "CMMD did not return an organization.",
    );
  }
}

function applyResolveOutcome(outcome: ResolveOutcome, setters: FirstRunResolveSetters): void {
  if (outcome.kind === "failed") {
    setters.setEnvironmentPhase("failed");
    setters.setEnvironmentError(outcome.message);
    return;
  }
  if (outcome.kind === "ask") {
    setters.setPendingOrganizationId(null);
    setters.setPendingWorkspaceLabel(null);
    setters.setWorkspaceOptions(outcome.options);
    setters.setEnvironmentPhase("confirm");
    return;
  }
  setters.setPendingOrganizationId(outcome.organizationId);
  setters.setPendingWorkspaceLabel(outcome.workspaceName);
  setters.setWorkspaceOptions([]);
  setters.setEnvironmentPhase("confirm");
}

export interface EnsureSetters {
  readonly setEnvironmentPhase: (phase: FirstRunEnvironmentPhase) => void;
  readonly setEnvironmentId: (id: string | null) => void;
  readonly setEnvironmentError: (message: string | null) => void;
}

/** Runs `ensure()`'s POST and applies whatever CMMD answers. */
export async function runEnsureAndApply(
  organizationId: number,
  setters: EnsureSetters,
): Promise<void> {
  try {
    const result = await ensureSeatEnvironment({
      organizationId,
      requestUrl: resolvePrimaryEnvironmentHttpUrl,
      requestInit: buildPrimaryFetchInit,
    });
    if (result.kind === "missing-seat") {
      setters.setEnvironmentPhase("dead-end");
      setters.setEnvironmentError(null);
      return;
    }
    if (result.kind !== "ok") {
      setters.setEnvironmentPhase("failed");
      setters.setEnvironmentError(result.message);
      return;
    }
    const snapshot = await snapshotForRuntime(result.runtimeId);
    if (!firstRunReadyFromSnapshot({ environmentId: result.environmentId, snapshot })) {
      setters.setEnvironmentId(result.environmentId);
      setters.setEnvironmentPhase("failed");
      setters.setEnvironmentError(CONTRACT_MISS_COPY);
      return;
    }
    setters.setEnvironmentId(result.environmentId);
    setters.setEnvironmentPhase("ready");
    setters.setEnvironmentError(null);
    refreshCmmdForgeRuntimeList();
  } catch (error) {
    setters.setEnvironmentPhase("failed");
    setters.setEnvironmentError(
      error instanceof Error ? error.message : "CMMD could not ensure an Environment.",
    );
  }
}

export interface FirstRunCatalogSource {
  readonly connected: boolean;
  readonly error: string | null;
  readonly fetchedAt: string | null;
  readonly environments: ReadonlyArray<{
    readonly environmentId: string | null;
    readonly cmmdEnvironmentId: string | null;
    readonly cmmdOrganizationId: number;
  }>;
}

export interface CatalogView {
  readonly environmentId: string | null;
  readonly organizationId: number | null;
  /**
   * A catalog fetch that hasn't actually settled (still the pre-fetch
   * INITIAL_SNAPSHOT, or a failed/auth-required load) reports the same "no
   * environment, not loading" shape a real empty catalog does. Resolving an
   * ensure target from bootstrap on that shape is exactly forge#3572 section
   * 3's early-fire gap: it can run before the account's real Environments
   * have loaded at all. Only a settled fetch — connected, no error, a real
   * `fetchedAt` — is trusted to mean "this account truly has no Environment
   * in this workspace yet."
   */
  readonly settled: boolean;
}

type FirstRunEnvironmentRow = FirstRunCatalogSource["environments"][number];

/**
 * A workspace not yet resolved has nothing scoped to filter by, so the very
 * first load (no persisted pick) reads the catalog exactly as before --
 * unscoped. Once a workspace is known, its own environments decide readiness
 * and its own registered-but-unprovisioned project decides the ensure
 * target; another workspace's rows must never leak into either.
 */
function filterRelevantEnvironments(
  environments: FirstRunCatalogSource["environments"],
  currentWorkspaceId: number | null,
): FirstRunCatalogSource["environments"] {
  if (currentWorkspaceId === null) return environments;
  return environments.filter(
    (environment) => environment.cmmdOrganizationId === currentWorkspaceId,
  );
}

function resolveFirstEnvironmentId(first: FirstRunEnvironmentRow | undefined): string | null {
  if (first === undefined) return null;
  if (first.cmmdEnvironmentId !== null) return first.cmmdEnvironmentId;
  return first.environmentId;
}

function isCatalogSettled(catalog: FirstRunCatalogSource): boolean {
  if (!catalog.connected) return false;
  if (catalog.error !== null) return false;
  return catalog.fetchedAt !== null;
}

export function deriveCatalogView(
  catalog: FirstRunCatalogSource,
  currentWorkspaceId: number | null,
): CatalogView {
  const relevant = filterRelevantEnvironments(catalog.environments, currentWorkspaceId);
  const first = relevant[0];
  return {
    environmentId: resolveFirstEnvironmentId(first),
    organizationId: first === undefined ? null : first.cmmdOrganizationId,
    settled: isCatalogSettled(catalog),
  };
}

export function resolveWorkspaceSelection(input: {
  readonly pendingOrganizationId: number | null;
  readonly workspaceOptions: ReadonlyArray<ForgeWorkspaceMembership>;
  readonly organizationId: number;
}): ForgeWorkspaceMembership | null {
  if (input.pendingOrganizationId !== null) return null;
  return input.workspaceOptions.find((option) => option.id === input.organizationId) ?? null;
}
